import {
  MENU_TO_PATH,
  getMenuIdFromPath,
  getParentMenuIdFromMenuId,
  expandMenuKeyAliases,
} from 'helpers/menuPermissionMap';

export const createMenuAccessChecker = (user) => {
  const cache = {};
  const permittedSet = new Set();

  // Build permission set from user permissions with all variants + aliases
  if (user?.permissions && user.permissions.length > 0) {
    (user.permissions || []).forEach((p) => {
      const key = (p.level_menu_limited || '').toString();
      if (!key) return;
      expandMenuKeyAliases(key).forEach((alias) => {
        permittedSet.add(alias);
        permittedSet.add(alias.replace(/_/g, '-'));
        permittedSet.add(alias.replace(/-/g, '_'));
        permittedSet.add(alias.replace(/^\/+/, ''));
        permittedSet.add(alias.replace(/^\/+/, '').replace(/-/g, '_'));
        permittedSet.add(alias.replace(/^\/+/, '').replace(/_/g, '-'));
      });
    });
  }

  const isOwner = user?.user_level && String(user.user_level).toLowerCase() === 'owner';
  const hasPermissionsFromBackend = permittedSet.size > 0;

  return (menuId, menuPath) => {
    // Create cache key
    const cacheKey = `${menuId}|${menuPath}`;
    if (cache[cacheKey] !== undefined) return cache[cacheKey];

    // ✅ Owner bypass semua permission
    if (isOwner) {
      cache[cacheKey] = true;
      return true;
    }

    // ✅ Tidak ada permission = sembunyikan semua menu
    if (!hasPermissionsFromBackend) {
      cache[cacheKey] = false;
      return false;
    }

    // =====================================================
    // STRATEGY 1: Check by menu ID directly
    // =====================================================
    const menuIdCandidates = expandMenuKeyAliases(menuId)
      .flatMap((id) => [id, id.replace(/-/g, '_'), id.replace(/_/g, '-')])
      .filter(Boolean);

    if (menuIdCandidates.some((c) => permittedSet.has(c))) {
      cache[cacheKey] = true;
      return true;
    }

    // =====================================================
    // STRATEGY 2: Check by path (with MENU_TO_PATH lookup)
    // =====================================================
    // Get path from menuPath or derive from menuId
    let routePath = menuPath;
    if (!routePath && menuId) {
      routePath = MENU_TO_PATH[menuId] || `/${menuId}`;
    }

    const normalizedPath = routePath?.startsWith('/')
      ? routePath
      : routePath
        ? `/${routePath}`
        : '';
    const pathWithoutSlash = normalizedPath.replace(/^\/+/, '');

    const pathCandidates = [
      menuPath,
      normalizedPath,
      pathWithoutSlash,
      pathWithoutSlash.replace(/-/g, '_'),
      pathWithoutSlash.replace(/_/g, '-'),
    ].filter(Boolean);

    if (pathCandidates.some((c) => permittedSet.has(c))) {
      cache[cacheKey] = true;
      return true;
    }

    // =====================================================
    // STRATEGY 3: Reverse lookup - check if menu ID's path is in permissions
    // =====================================================
    if (menuId) {
      // Try to find menuId in MENU_TO_PATH (menuId -> path)
      const menuIdPath = MENU_TO_PATH[menuId];
      if (menuIdPath) {
        const menuIdPathCandidates = [
          menuIdPath,
          menuIdPath.replace(/^\/+/, ''),
          menuIdPath.replace(/-/g, '_'),
        ];
        if (menuIdPathCandidates.some((c) => permittedSet.has(c))) {
          cache[cacheKey] = true;
          return true;
        }
      }

      // Also check if menuId itself matches any permission directly
      // This handles the case where permission is stored as menu ID
      const menuIdDirectCandidates = [menuId, menuId.replace(/-/g, '_'), menuId.replace(/_/g, '-')];
      if (menuIdDirectCandidates.some((c) => permittedSet.has(c))) {
        cache[cacheKey] = true;
        return true;
      }
    }

    // =====================================================
    // STRATEGY 4: Parent menu variants (e.g., dashboard-member -> dashboard)
    // =====================================================
    if (pathWithoutSlash) {
      const parts = pathWithoutSlash.split('-');
      if (parts.length > 1) {
        const parentCandidates = ['/' + parts[0], parts[0], parts[0].replace(/-/g, '_')].filter(
          Boolean
        );
        if (parentCandidates.some((c) => permittedSet.has(c))) {
          cache[cacheKey] = true;
          return true;
        }
      }
    }

    // =====================================================
    // STRATEGY 5: Check via getMenuIdFromPath for dynamic paths
    // =====================================================
    if (menuPath) {
      const derivedMenuId = getMenuIdFromPath(menuPath);
      if (derivedMenuId && derivedMenuId !== menuId) {
        const derivedCandidates = [
          derivedMenuId,
          derivedMenuId.replace(/-/g, '_'),
          derivedMenuId.replace(/_/g, '-'),
        ];
        if (derivedCandidates.some((c) => permittedSet.has(c))) {
          cache[cacheKey] = true;
          return true;
        }
      }
    }

    // =====================================================
    // STRATEGY 6: Check for parent menu permission (for sub-menu items)
    // e.g., message-customer -> message (parent), monitoring-customer-history-pin -> monitoring-customer
    // =====================================================
    if (menuId) {
      const parentMenuId = getParentMenuIdFromMenuId(menuId);
      if (parentMenuId) {
        const parentCandidates = [
          parentMenuId,
          parentMenuId.replace(/-/g, '_'),
          parentMenuId.replace(/_/g, '-'),
          '/' + parentMenuId,
        ];
        if (parentCandidates.some((c) => permittedSet.has(c))) {
          cache[cacheKey] = true;
          return true;
        }
      }
    }

    cache[cacheKey] = false;
    return false;
  };
};
